import { ECarts } from '@base/utils/constants'
import type { ISalesFilter } from '@amirada/lib-wilo-shared'
import { ESubTab, ETab } from '@amirada/lib-wilo-shared'

// The product list is a tab ModalPDF adds itself — it has no node and therefore no entry in ETab.
export const def_tabProductList: string = 'range_productlist'

/*
 * How wide the photo and the curve of ONE comparison column are printed, in px.
 *
 * ⚑ One value for both, and it has to stay that way: photo (`Compare.vue`) and curve
 * (`PumpCurve.vue`) sit in the same column of the same table, and two different widths make the
 * column jump.
 *
 * The budget is fixed: A4 is 210 mm and pdf.ts prints it with 20 mm margins left and right
 * (`extension/pdf.ts`), which leaves 170 mm ≈ 643 px of content. The print grouping puts three
 * products next to each other (`ut_wilo_compareTablePrintGroups`, maxItems 3), each cell adding
 * `print:px-3` = 24 px, and the property column takes the rest. 148 px did not fit and pushed the
 * third product off the page; 133 px does.
 */
export const def_printCompareImageWidth: number = 133

/*
 * How many products one printed comparison table holds before the next group starts.
 *
 * ⚑ It travels in TWO places that have to agree: the split itself
 * (`ut_wilo_compareTablePrintGroups`) and the `startIndex` every group after the first has to be
 * given, because `Compare` reads the curve of a column out of `param4` by its position in the
 * WHOLE list (`printIndex`), not in the group. Cart.vue wrote the 3 out twice and stayed right;
 * TabReplacement.vue wrote it nowhere and silently handed group 2 the curves of group 1.
 */
export const def_printCompareGroupSize: number = 3

// Hosts that make this an internal client: price properties survive the country restriction there.
export const def_internalHosts: string[] = ['solutions4.wilo.com', 'solutions.wilo.com']

// Markets that offer only a subset of the languages the server delivers, keyed by market (rcco).
// A market without an entry here keeps the full list. Used by ut_wilo_filteredLangData().
export const def_marketLanguages: Record<string, readonly string[]> = {
  _oem_hvac: ['en', 'fr', 'de', 'es', 'it', 'zh', 'tr', 'nl'],
}

export const enum EIconSize {
  item = 'item',
  table = 'table',
}

export const enum ECompareType {
  series = 'series',
  products = 'products',
  none = 'none',
}

// TEMP (testing): internal users (by MSAL email) allowed to see the gated internal test features
// when embedded — the new range subtabs (Detail, ModalPDF) and the Digital product pass link
// (Navigation). The "Price list" switch and the disclaimer edit are no longer gated by it
// (15.09.2026). Remove this list + its uses after the test phase. Checked via ut_wilo_isAllowlistedUser().
//
// Lowercase by convention, but the check no longer depends on it: three entries here carried
// capitals and therefore matched nobody, silently, until 07.09.2026.
export const def_internalUserAllowlist: readonly string[] = [
  'c.froemter@amirada.com',
  'anja.plessgott@wilo.com',
  'thomas.funk@wilo.com',
  'lukas.lange@wilo.com',
  'anita.freiherr@wilo.com',
  'tobias.gross@wilo.com',
  'nikolai.gellert@wilo.com',
  'christian.vonnethen@wilo.com',
  'andre.nickel@wilo.com',
]

// Internal users (by MSAL email) who see the Wilo World import parameter (path + psx) on level2
// application pages — only together with isInternal. Checked via ut_wilo_isWiloWorldUser(); like the
// allowlist above, both sides are lowercased, so capitals here are harmless.
export const def_wiloWorldlist: readonly string[] = [
  'c.froemter@amirada.com',
  'anja.plessgott@wilo.com',
  'christian.vonnethen@wilo.com',
]

export const ECartPageText: Partial<Record<ECarts, string>> = {
  [ECarts.ct_shopping_cart]: 'shoppingcart',
  [ECarts.ct_catalogue_create]: 'catalogcreate',
  [ECarts.ct_price_list_create]: 'pricelistcreate',
  [ECarts.ct_favourites]: 'favorites',
  [ECarts.ct_product_compare]: 'productcompare',
  [ECarts.ct_download_list]: 'downloadlist',
}

export const ECartIconAr: Partial<Record<ECarts, string>> = {
  [ECarts.ct_shopping_cart]: 'cart-shopping-solid-full',
  [ECarts.ct_catalogue_create]: 'file-plus-solid-full',
  [ECarts.ct_price_list_create]: 'file-invoice-dollar-solid-full',
  [ECarts.ct_favourites]: 'star-solid-full',
  [ECarts.ct_product_compare]: 'code-compare-solid-full',
  [ECarts.ct_download_list]: 'file-excel-solid-full',
}

export const PressureUnits = [
  'bar',
  'kPa',
  'psi',
]

export const FlowUnits = [
  'm³/h',
  'l/s',
  'l/h',
  'l/min',
  'm³/min',
  'm³/s',
  'US g.p.m.',
  'US MGD',
]

export const HeadUnits = [
  'm',
  'ft',
]

export const enum ECartUserDefined {
  cart_us_dutyPoint = 'dp',
  cart_us_priceTable = 'pt',
  cart_us_content = 'ct',
  // Hidden from the print, kept in the cart. Set on EVERY affected row, not only on the marker that
  // was clicked: the state is then readable off a single row wherever it is needed (the PDF handover
  // in Cart.vue does not have to know the hierarchy at all). Decision of 24.08.2026.
  cart_us_hidden = 'hd',
  // The row's own id — price list only, where the same series may stand more than once (24.09.2026).
  // The namePath says WHICH object a row is, this says WHICH ROW; see composables/priceListCart.ts.
  cart_us_rowId = 'rid',
}

/*
 * The account a saved project is filed under when nobody is signed in.
 *
 * `groupId` on CART_CREATE/CART_LIST is normally the MSAL `homeAccountId`. Without a sign-in there
 * is none, and the price list could be built but neither saved nor found again — silently. This id
 * stands in. Deliberately readable rather than a GUID, so shared/test projects are recognisable at
 * a glance in the store.
 */
export const def_testAccountId: string = 'dev-test-account'

/**
 * Markets where certain texts must be hidden for now, because they may not comply with the EU
 * directive on empowering consumers for the green transition (EmpCo). Source: the customer's list
 * `EmpCo_Märkte.xlsx` (24.09.2026) — every row counts; its "EmpCO betroffen" column is empty.
 *
 * Codes of `restData.rcco` (without the `_H` of the historic markets), LOWER case like the rcco the
 * client carries — not the list's own column where that one is wrong (Christoph, 24.09.2026): "UK"
 * is gb, North Macedonia was listed as RS (Serbia's code, which has its own row) and is mk, "OEM" is
 * _oem_hvac.
 *
 * Read through ut_wilo_isEmpCo(realCountry), never directly.
 *
 * ⚑ The same list exists twice more on the server, deliberately (temporary): pfinderServer
 * services/pfinder-wilo/src/extension/empCo.ts and services/esearch/src/empCo.ts. Change all three or none.
 */
export const def_empCoCountries: readonly string[] = [
  'al', 'at', 'ba', 'be', 'bg', 'ch', 'cz', 'de', 'dk', 'ee', 'es', 'fi', 'fr', 'gb', 'gr', 'hr', 'hu',
  'ie', 'it', 'lt', 'lv', 'mk', 'nl', 'no', 'pl', 'pt', 'ro', 'rs', 'se', 'si', 'sk', 'ua', '_oem_hvac',
]

/*
 * Stands in the url for the one kind of open project that has no uuid: a template before "Enable
 * edit". It is only there so that opening one still creates a history entry and the browser back
 * button reaches the project management; a reload on it falls back to the management and cleans the
 * url. "Enable edit" creates the record, and its uuid replaces this value. A custom price list never
 * stands on it — it is created before it opens (`usePriceListProject#openProject`).
 */
export const def_newProjectUrlId: string = 'new'

// The series-description blocks a price list shows, by `cod`, in no particular order — they are
// rendered in the order the data has. A POSITIVE list on purpose: a block added in CMI must not
// appear by itself in a document that carries prices, so anything not named here stays out.
//
// Since 31.08.2026 they no longer appear in a tab of their own but in the series header, next to the
// photo; the list is read by ut_wilo_seriesDescriptionComps.
//
// ⚑ These cod values are maintained by the customer and are not enforced unique by the system. A
// rename does not fail loudly — the block simply does not appear. Start looking here, not in the
// import, when the construction type or the type key is missing from a price list.
//   Design  — construction type ("Bauart" in CMI)
//   Typekey — type key; already the handle ut_wilo_splitDescription uses to keep the type key from
//             being split at <P><B>
export const def_priceListDescriptionComps: string[] = ['Design', 'Typekey']

// What a price list prints per series. Fixed — unlike every other PDF, the customer gets no page
// selection here.
//
// The strings are the values ModalPDF's getTabs produces: a tab PSM, 'tab|subtab' for a subtab, plus
// the two synthetic ones it invents ('series-info' for the header block, 'range_productlist' for the
// list it always adds). They are matched against that result rather than written into the url
// directly, which is what makes "only if present" hold: a series without one of them contributes
// nothing instead of producing an empty page.
// ⚑ `range_description` is NOT in here (since 31.08.2026), although two of its blocks are printed:
// construction type and type key moved into the series-info header, where they sit next to the
// photo. The tab as a whole would repeat them and bring six more blocks with them.
export const def_priceListTabs: string[] = [
  'series-info',
  [ETab.rangeAdvantages, ESubTab.rangeAccessory].join('|'),
  [ETab.rangeAdvantages, ESubTab.rangeServices].join('|'),
  def_tabProductList,
]

/*
 * The entries of def_priceListTabs the customer may switch off in the price-list pdf (25.09.2026):
 * accessories and services. Offered as two more boxes after the sales boxes in ModalPDF, ticked by
 * default; an unticked one is left out of the fixed set when the pdf is made. Series info and the
 * product list are what a price list is, so they stay fixed.
 */
export const def_priceListTabOptions: { value: string, text: string }[] = [
  { value: [ETab.rangeAdvantages, ESubTab.rangeAccessory].join('|'), text: 'txt_range-accessories' },
  { value: [ETab.rangeAdvantages, ESubTab.rangeServices].join('|'), text: 'txt_range-services' },
]

/*
 * The graphic-only subtabs of product_technical added on 13.09.2026 (rendered by TabGraphics).
 * ⚑ TEMPORARY (18.09.2026): not live yet — shown only standalone or internal, never embedded on
 * wilo.com. The detail page (detailTabs2) and ModalPDF (getTabs) filter them; drop this list and
 * both conditions once they go live.
 */
export const def_newGraphicSubTabs: string[] = [
  ESubTab.productFlowAndTerminalBoxOrientations,
  ESubTab.productConnectionDrawing,
  ESubTab.productConstructionDirectionOfRotation,
  ESubTab.productDimensionDrawingStationaryDryWellInstallation,
  ESubTab.productDimensionDrawingStationaryWetWellInstallation,
  ESubTab.productDimensionDrawingPortableDryWellInstallation,
  ESubTab.productDimensionDrawingPortableWetWellInstallation,
  ESubTab.productConnectionDiagramPlug,
]

/*
 * The sales information a pdf can leave out — in ONE place, because it is one statement that would
 * otherwise be made four times: the flag inside PARAM.salesFilter, the checkbox value in ModalPDF,
 * the text key of its label, and the cods that identify the columns.
 *
 * Kept together because every way of getting them apart fails QUIETLY. A checkbox writing a flag
 * nobody reads, or a flag whose columns are recognised under a different cod, does not throw — the
 * pdf simply carries one column more than the customer asked for, and nobody reports that.
 *
 * `priceListOnly` marks the two that can only ever appear in a price list: their columns reach the
 * client through `custom.klc` and by no other route, so offering the choice in an ordinary pdf
 * would be a switch for something that cannot show up.
 *
 * ⚑ `cods` is empty for the price, and that is not an oversight: what counts as a price is
 * `ut_wilo_isPriceProp`'s answer and stays there, because the same function also decides
 * FORMATTING. A delivery time run through the price formatter would be JSON.parsed and come back
 * as "price on request".
 *
 * ⚑ The cods match EXACTLY, never by prefix (decision 09.09.2026). Delivery time has a variant
 * matrix of twelve, like the price (`oc_supply_availability_H1_SFE` …), and only the base property
 * is ever a column — while a prefix would additionally catch
 * `Copy_From_oc_supply_availability__SFE`, which is the FACTORY GUARANTEE, not a delivery time.
 * If a variant ever becomes a column, it is entered here.
 */
export interface ISalesInfoOption {
  flag: keyof ISalesFilter
  value: string // the checkbox value in ModalPDF, and the stem of its text key
  text: string // GUIText key of the label — handed to the customer for translation
  priceListOnly: boolean
  cods: string[]
}

export const def_salesInfoOptions: ISalesInfoOption[] = [
  { flag: 'noPrice', value: 'price-information', text: 'txt_price-information', priceListOnly: false, cods: [] },
  {
    flag: 'noPriceGroup',
    value: 'price-group',
    text: 'txt_price-group',
    priceListOnly: true,
    cods: ['sales_price_category', 'sales_price_category_SFE', 'sales_price_category_series', 'sales_price_category_acc_series'],
  },
  /*
   * ⚑ `oc_supply_availability_invisible_SFE`, NOT `oc_supply_availability__SFE`. The second is the
   * older property on the series' own sales data and is out (decision 09.09.2026); the delivery
   * time a price list prints is the one the product-list template carries — measured in the
   * delivered tree, desc "Lieferzeit", under Copy_(2)_From_pumplist_…, and therefore the column
   * this filter has to be able to remove.
   *
   * Not in here on purpose: the eleven variants of the matrix (`_H1`, `_S2`, …). Only the base
   * property is ever a column.
   */
  { flag: 'noDelivery', value: 'delivery-time', text: 'txt_delivery-time', priceListOnly: true, cods: ['oc_supply_availability_invisible_SFE'] },
]

// The price group as the accessory table prints it. One of the four cods above — the one the
// accessory data actually carries (base_after.csv puts it on accessory_table_comp), and the one
// whose description the scripting copies so the column can caption itself.
export const def_accPriceGroupCod: string = 'sales_price_category'

/*
 * The price group a SERIES carries, under range_default/range_default_additional.
 *
 * ⚑ EITHER-OR, never both: an ordinary series carries the first, an ACCESSORY series the second.
 * Two cods are listed because the reader cannot know which kind of series it is looking at — not
 * because two lines could appear. Exactly one of them is there, so exactly one line comes out, and
 * a series showing only one is the normal case rather than a gap in the data.
 *
 * Both belong to the price-group family above, so the sales filter already covers them and no
 * second rule decides whether they may be shown.
 */
export const def_seriesPriceGroupCods: string[] = ['sales_price_category_series', 'sales_price_category_acc_series']

// Non-DB content elements that can be inserted between cart items (catalog structure).
export const enum ECartContentType {
  cc_chapter = 'chapter',
  cc_subchapter = 'subchapter',
  cc_group = 'group',
  cc_pdf = 'pdf',
}

// Visual mapping per content marker type (icon, row background incl. weight, default label, text key)
// and its place in the hierarchy (`level`: chapter 1 < subchapter 2 < group 3 < a leaf 4 — a pdf row,
// like every series row, owns nothing below it). Shared by the price-list view (CartSeriesPriceList),
// the cart sort view and the pdf handover (ModalPDF), so all of them read the marker alike.
//
// `label` is the default name STORED on a hand-made marker (ICartContent.label) — English, and kept
// that way: saved projects carry it, and ModalPDF recognises an unnamed PDF row by it. What the user
// SEES for a marker without a text of its own is `textKey` through ut_getText (24.09.2026), so a new
// chapter reads in the app's language (ut_wilo_contentLabel).
// The two catalogs the price list picks series from (catalog modal, CartPriceListInsertBar). namePath
// incl. root13; rcco/lcda are only route, not part of it.
export const def_priceListCatalogProducts: string = 'root13/8ae2819c8e9774cf018ea36fa2c86c6b'
export const def_priceListCatalogAccessories: string = 'root13/8ae2819c8e9774cf018ea370051d1130'

export const def_cartContentMeta: Record<ECartContentType, { icon: string, color: string, label: string, textKey: string, level: number }> = {
  [ECartContentType.cc_chapter]: { icon: 'i-wilo-book-solid-full', color: 'bg-gunmetal-2 font-bold', label: 'Chapter', textKey: 'txt_pricelist-chapter', level: 1 },
  [ECartContentType.cc_subchapter]: { icon: 'i-wilo-book-open-cover-solid-full', color: 'bg-gunmetal-3 font-bold', label: 'Subchapter', textKey: 'txt_pricelist-subchapter', level: 2 },
  [ECartContentType.cc_group]: { icon: 'i-wilo-layer-group-solid-full', color: 'bg-gunmetal-4', label: 'Group', textKey: 'txt_pricelist-group', level: 3 },
  [ECartContentType.cc_pdf]: { icon: 'i-wilo-file-circle-plus-solid-full', color: 'bg-gunmetal-5', label: 'PDF', textKey: 'txt_pricelist-pdf', level: 4 },
}

// The level of everything that is not a structure marker: a series row, a pdf row. Owns nothing below it.
export const def_cartLeafLevel: number = 4

// --- Predefined price list (structure exported from CMI, see IPriceListNode) ---------------------
//
// These two type codes ARE the series — the only nodes that become real cart positions and the only
// ones that need a namePath. The test is a whitelist, and so is def_priceListContentType below: a
// code the CMI adds later is in neither and is ignored with its subtree — never mistaken for a
// series, never turned into a guessed marker.
//
// The codes travel as data (they live in sys4/CMI), so this is the one place in the client that
// names them — nothing else looks at typeCod.
export const def_priceListSeriesTypes: string[] = ['Series_Link', 'Accessory_Series_Link']

// Which marker level a structure node becomes. ⚑ No fallback (since 18.09.2026): a code that is
// neither in here nor a series is ignored together with its whole subtree (CartManage isKnownNode).
export const def_priceListContentType: Record<string, ECartContentType> = {
  Application: ECartContentType.cc_chapter,
  Sub_Application: ECartContentType.cc_subchapter,
  Sub_application_Technologies: ECartContentType.cc_group,
}

export const enum EUserUserDefined {
  user_us_priceList = 'pricelist',
}

export const enum ESpaixType {
  sp_compare = 'cp_',
  sp_replacement = 'rp_',
  sp_quickselect = 'qs_',
  sp_modal = 'md_',
}

export const enum PARAM_LCC {
  dataUrl = 'param1',
  pageName = 'param2',
  tablePageNumber = 'param3',
}

export const enum EItemState {
  st_none = '',
  st_soon = 'coming-soon',
  st_new = 'new',
  st_discontinued = 'to-be-discontinued',
  st_expired = 'expired',
}

export const enum EPrintRes {
  pr_print = 'print',
  pr_screen = 'screen',
}

// A standard cover image of the pdf, relative to def_coverPicsRoot. `srcPrint` is the high-resolution
// version and is used only when the output is set to print; an entry without one keeps `src` in both
// cases. Here and not in ModalPDF since the project tiles of the price list show the cover too.
export type TCoverPic = { src: string, srcPrint?: string }
export const def_coverPicsRoot: string = 'https://cms.media.wilo.com/web_cover_for_oc/'
// The first entry is the default (and the fallback when nothing else gives a cover).
// 25.09.2026 (Christoph): wilo442641 deactivated, wilo801645 (formerly 7th) moved to the front.
export const def_coverPics: TCoverPic[] = [
  { src: 'wilo801645/11705426/wilo801645_1.jpg' },
  // { src: 'wilo442641/11476379/wilo442641_1.jpg', srcPrint: 'wilo442641/11476383/wilo442641_1.jpg' },
  { src: 'wilo515153/11463339/wilo515153_1.jpg' },
  { src: 'wilo516487/11476461/wilo516487_1.jpg' },
  { src: 'wilo515142/11476465/wilo515142_1.jpg' },
  { src: 'wilo515140/11476469/wilo515140_1.jpg' },
  { src: 'wilo515156/11476473/wilo515156_1.jpg' },
  { src: 'wilo798503/11705430/wilo798503_1.jpg' },
]
